import { Box, Button, Typography } from '@mui/material';
import React from 'react';

/**
 * FormErrorBoundary component to catch JavaScript errors in ActiveCampaign form embeds
 * Provides a lightweight, inline error UI instead of taking over the whole page
 *
 * @class FormErrorBoundary
 * @augments {React.Component}
 */
class FormErrorBoundary extends React.Component {
  /**
   * Creates an instance of FormErrorBoundary
   * @param {Object} props - Component props
   * @param {React.ReactNode} props.children - Child components to render
   * @param {string} [props.fallbackMessage] - Custom error message to display
   */
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  /**
   * Update state when an error is caught
   * @param {Error} error - The error that was thrown
   * @returns {Object} New state object
   */
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  /**
   * Log error details to console
   * @param {Error} error - The error that was thrown
   * @param {Object} errorInfo - Additional error information
   */
  componentDidCatch(error, errorInfo) {
    console.error('FormErrorBoundary caught an error:', error, errorInfo);
  }

  /**
   * Reset error state to try rendering again
   */
  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  /**
   * Render error fallback or children
   * @returns {React.ReactNode}
   */
  render() {
    if (this.state.hasError) {
      return (
        <Box
          sx={{
            p: 4,
            textAlign: 'center',
            backgroundColor: 'rgba(255, 243, 205, 0.15)',
            border: '1px solid rgba(255, 193, 7, 0.4)',
            borderRadius: '8px',
            my: 2,
          }}
        >
          <Typography
            variant="h6"
            sx={{ color: '#ffc107', fontWeight: 600, mb: 2 }}
          >
            {this.props.fallbackMessage || 'Unable to load form'}
          </Typography>
          <Typography
            sx={{
              color: 'rgba(255, 255, 255, 0.7)',
              mb: 3,
              fontSize: '0.95rem',
            }}
          >
            There was an error loading the contact form. Please try refreshing
            the page or contact us directly at{' '}
            <a
              href="mailto:info@ella.fi"
              style={{
                color: 'var(--color-secondary-light)',
                textDecoration: 'underline',
              }}
            >
              info@ella.fi
            </a>
          </Typography>
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              justifyContent: 'center',
              flexWrap: 'wrap',
            }}
          >
            <Button
              variant="contained"
              onClick={this.handleReset}
              sx={{
                backgroundColor: 'var(--color-secondary)',
                color: 'var(--color-secondary-text)',
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: 'var(--color-secondary-light)',
                },
              }}
            >
              Try Again
            </Button>
            <Button
              variant="outlined"
              onClick={() => window.location.reload()}
              sx={{
                borderColor: 'var(--color-secondary-light)',
                color: 'var(--color-secondary-light)',
                textTransform: 'none',
                '&:hover': {
                  borderColor: 'var(--color-secondary)',
                  backgroundColor: 'rgba(126, 148, 25, 0.08)',
                },
              }}
            >
              Refresh Page
            </Button>
          </Box>

          {/* Show error details in development */}
          {process.env.NODE_ENV === 'development' && this.state.error && (
            <Box
              sx={{
                mt: 3,
                p: 2,
                backgroundColor: 'rgba(0, 0, 0, 0.2)',
                borderRadius: '4px',
                textAlign: 'left',
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontFamily: 'monospace',
                  fontSize: '0.75rem',
                  color: '#ff6b6b',
                  display: 'block',
                  wordBreak: 'break-word',
                }}
              >
                {this.state.error.toString()}
              </Typography>
            </Box>
          )}
        </Box>
      );
    }

    return this.props.children;
  }
}

export default FormErrorBoundary;
